import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';

const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const qty = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));
const num = (v) => Number(v || 0);

/** Paper colours whatever the app's theme, so it looks and prints like a purchase order. */
const STYLE = `
.po-doc { background:#fff; color:#111; font-size:12px; line-height:1.35; padding:16px; border:1px solid #222; max-width:900px; margin:0 auto; }
.po-doc table { width:100%; border-collapse:collapse; color:#111; }
.po-doc th, .po-doc td { border:1px solid #444; padding:3px 5px; vertical-align:top; }
.po-doc th { background:#f1f1f1; font-weight:600; }
.po-doc .po-noborder td { border:none; padding:1px 0; }
.po-doc .po-title { text-align:center; font-size:17px; font-weight:700; letter-spacing:.06em; margin:0; }
.po-doc .po-name { font-size:16px; font-weight:700; }
.po-doc .po-muted { color:#444; }
.po-doc .po-label { font-size:10.5px; text-transform:uppercase; letter-spacing:.04em; color:#555; }
.po-doc .num { text-align:right; white-space:nowrap; }
.po-doc .po-sign { height:60px; }
@media print { .po-doc { border:1px solid #222; max-width:none; } }
`;

/** Vendors named on a PO's lines, in line order (a PO can name several; each gets their own copy). */
export function poVendors(po) {
  const seen = new Map();
  (po?.lineItems || []).forEach((li) => { if (li.vendor && !seen.has(li.vendor.id)) seen.set(li.vendor.id, li.vendor); });
  return [...seen.values()];
}

/**
 * The purchase order as the vendor gets it: buyer and vendor with GSTINs and states, PO number and dates, where to
 * deliver, the lines with HSN, quantity, unit, rate and amount, the total in words, payment terms, the organization's
 * purchase order terms and the signature block. Shown in the print view and on the page a shared link opens.
 */
export function PurchaseOrderPaper({ doc, currency = 'INR' }) {
  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v) => (v == null ? '' : f.format(num(v)));
  }, []);
  const b = doc.buyer || {};
  const v = doc.vendor || {};
  const t = doc.totals || {};
  const d = doc.deliverTo;
  const currencySymbol = currency === 'INR' ? '₹' : `${currency} `;

  return (
    <div className="po-doc">
      <style>{STYLE}</style>
      <p className="po-title">{doc.title}</p>
      {doc.draft && <p className="po-title" style={{ color: '#b00', fontSize: 13 }}>DRAFT, NOT YET APPROVED</p>}

      <table style={{ marginTop: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: '58%' }}>
              <div className="d-flex gap-2">
                {b.logoUrl && <img src={b.logoUrl} alt="" style={{ maxHeight: 56, maxWidth: 110, objectFit: 'contain' }} />}
                <div>
                  <div className="po-name">{b.name}</div>
                  {b.legalName && <div>{b.legalName}</div>}
                  {b.address && <div className="po-muted">{b.address}</div>}
                  {b.gstin && <div><b>GSTIN:</b> {b.gstin}</div>}
                  {b.state && <div><b>State:</b> {b.state}</div>}
                  {b.pan && <div><b>PAN:</b> {b.pan}</div>}
                  {(b.phone || b.email) && <div className="po-muted">{[b.phone, b.email].filter(Boolean).join(' · ')}</div>}
                </div>
              </div>
            </td>
            <td>
              <table className="po-noborder">
                <tbody>
                  <tr><td className="po-label">PO no.</td><td><b>{doc.poNumber}</b></td></tr>
                  <tr><td className="po-label">Date</td><td>{day(doc.poDate)}</td></tr>
                  {doc.requiredDate && <tr><td className="po-label">Deliver by</td><td><b>{day(doc.requiredDate)}</b></td></tr>}
                  {doc.reference && <tr><td className="po-label">Reference</td><td>{doc.reference}</td></tr>}
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <td colSpan={d ? 1 : 2}>
              <div className="po-label">To (vendor)</div>
              {doc.vendor ? (
                <>
                  <div><b>{v.name}</b>{v.contactPerson ? <span className="po-muted"> · Attn. {v.contactPerson}</span> : null}</div>
                  {v.address && <div className="po-muted">{v.address}</div>}
                  {v.gstin && <div><b>GSTIN:</b> {v.gstin}</div>}
                  {v.state && <div><b>State:</b> {v.state}</div>}
                  {(v.phone || v.email) && <div className="po-muted">{[v.phone, v.email].filter(Boolean).join(' · ')}</div>}
                </>
              ) : <div className="po-muted">No vendor on this order</div>}
            </td>
            {d && (
              <td>
                <div className="po-label">Deliver to</div>
                <div><b>{d.name}</b></div>
                {d.address && <div className="po-muted">{d.address}</div>}
                {d.phone && <div className="po-muted">{d.phone}</div>}
              </td>
            )}
          </tr>
        </tbody>
      </table>

      <table style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>#</th><th>Description</th><th>HSN</th><th className="num">Qty</th><th>Unit</th><th className="num">Rate</th><th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {(doc.lines || []).map((l) => (
            <tr key={l.sr}>
              <td>{l.sr}</td>
              <td>
                {l.item}
                {l.description && <div className="po-muted">{l.description}</div>}
                {l.remarks && <div className="po-muted">{l.remarks}</div>}
              </td>
              <td>{l.hsn || ''}</td>
              <td className="num">{qty(l.quantity)}</td>
              <td>{l.unit || ''}</td>
              <td className="num">{money(l.rate)}</td>
              <td className="num">{money(l.amount)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600 }}>
            <td colSpan={3}>Total</td>
            <td className="num">{t.quantity != null ? qty(t.quantity) : ''}</td>
            <td>{t.quantity != null ? t.unit : ''}</td>
            <td />
            <td className="num">{currencySymbol}{money(t.total)}</td>
          </tr>
        </tbody>
      </table>

      <table style={{ marginTop: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: '58%' }}>
              <div className="po-label">Amount in words</div>
              <div><b>{t.totalInWords}</b></div>
              {doc.paymentTerms && (
                <div style={{ marginTop: 6 }}><span className="po-label">Payment </span>{doc.paymentTerms}</div>
              )}
              {doc.terms && (
                <div style={{ marginTop: 6 }}>
                  <div className="po-label">Terms and conditions</div>
                  <div style={{ whiteSpace: 'pre-line' }}>{doc.terms}</div>
                </div>
              )}
              {doc.remarks && <div style={{ marginTop: 6 }}><span className="po-label">Remarks </span>{doc.remarks}</div>}
              <div className="po-muted" style={{ marginTop: 6 }}>Please quote the PO number on your bill and delivery challan.</div>
            </td>
            <td>
              <div style={{ textAlign: 'right' }}>For <b>{b.signatory || b.name}</b></div>
              <div className="po-sign" />
              <div style={{ textAlign: 'right' }}>Authorised Signatory</div>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="po-muted" style={{ margin: '6px 0 0', textAlign: 'center', fontSize: 10.5 }}>This is a computer generated purchase order.</p>
    </div>
  );
}

/**
 * The print view of a purchase order: one vendor's copy at a time (choose the vendor when the PO names several), with
 * "Send to vendor" for an approved order.
 */
function PurchaseOrderPrint({ po, currency = 'INR', onSend }) {
  const vendors = useMemo(() => poVendors(po), [po]);
  const [vendorId, setVendorId] = useState(vendors[0]?.id || '');
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null);
    setError(null);
    axios.get(`/api/purchase-orders/${po.id}/print`, { params: vendorId ? { vendorId } : {} })
      .then((r) => setDoc(r.data))
      .catch((e) => setError(typeof e.response?.data === 'string' && e.response.data ? e.response.data : 'Could not load the purchase order'));
  }, [po.id, vendorId]);

  const sendable = !['DRAFT', 'CANCELLED'].includes(po.status) && vendorId;
  return (
    <div>
      <div className="print-actions d-flex flex-wrap align-items-center gap-2 mb-2">
        {vendors.length > 1 && (
          <>
            <Form.Label className="small mb-0">Vendor&apos;s copy</Form.Label>
            <Form.Select size="sm" style={{ width: 'auto' }} value={vendorId} onChange={(e) => setVendorId(Number(e.target.value))}>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Form.Select>
          </>
        )}
        {onSend && sendable && (
          <Button size="sm" variant="outline-success" onClick={() => onSend(po, vendors.find((v) => v.id === vendorId))}>
            <i className="bi bi-send me-1" />Send to vendor
          </Button>
        )}
        {po.status === 'DRAFT' && <span className="small text-warning">Approve the order before sending it to the vendor.</span>}
      </div>
      {error ? <p className="text-danger">{error}</p>
        : !doc ? <div className="text-center py-4"><Spinner animation="border" /></div>
          : <PurchaseOrderPaper doc={doc} currency={currency} />}
    </div>
  );
}

export default PurchaseOrderPrint;
